// Claymation Ad's draft as the form keeps it (spec G14-claymation-ad, PR d1): the server holds the
// member's draft (`.../templates/claymation-ad/draft`), so it survives the panel; the form reads
// it, keeps the script stage's edits there, and asks the server's pack reader and script director
// (live's `useClaymationDraft`, which ran them from the browser), then the takes, the shot
// director and the stills. As Pixar Talking Ad's (`pixar-draft.ts`), with the takes and the
// storyboard's stills counted in the draft's work.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetClaymationAdDraftQueryKey,
  useAddClaymationAdTake,
  useApproveClaymationAdScript,
  useApproveClaymationAdVoice,
  useBackToClaymationAdVoice,
  useGetClaymationAdDraft,
  useRegenerateClaymationAdStill,
  useResetClaymationAdDraft,
  useRetryClaymationAdShotDirector,
  useWriteClaymationAdDraft,
  useWriteClaymationAdScript,
  writeClaymationAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  ClaymationAdDraft,
  ClaymationScript,
  ClaymationStillName,
  OutputRef,
} from "../../../api/generated/model";
import {
  ClaymationAdDraftWrite,
  ClaymationAdInputs,
  ClaymationAdScriptApproval,
  ClaymationAdScriptAsk,
  type ClaymationAdDraftWriteOutput,
  type ClaymationAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface ClaymationForm {
  inputs: ClaymationAdInputsOutput;
  script: ClaymationScript | null;
}

/** The draft refetched while it works: every 2 s while a director, a take or a still works, and a
 * moment after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** Where the draft's work stands, as live's progress line names it (`busyByStage`). */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  takes: { done: number; total: number } | null;
  planning: boolean;
  drawing: { done: number; total: number } | null;
}

const IDLE: DraftWork = { reading: false, writing: false, takes: null, planning: false, drawing: null };

/** Steps of which some are under way: working, or pending on one that is (a still waits for those
 * it is drawn from or after; one pending behind a failed still waits for good). */
function underWay(statuses: readonly string[]): { done: number; total: number } | null {
  const going =
    statuses.includes("working") || (statuses.includes("pending") && !statuses.includes("failed"));
  return going ? { done: statuses.filter((status) => status === "done").length, total: statuses.length } : null;
}

export function workOf(draft: ClaymationAdDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    takes: underWay(draft.takes.map((take) => take.status)),
    planning: draft.shot_director === "working",
    drawing: underWay(draft.stills.map((still) => still.status)),
  };
}

export function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.takes !== null || work.planning || work.drawing !== null;
}

/** Live's progress line under the form (`run-progress.ts`), the draft's part. */
export function progressOf(work: DraftWork): string | null {
  if (work.reading) return "Reading the pack";
  if (work.writing) return "Writing the script";
  if (work.takes !== null) return `Making the voiceover takes · ${work.takes.done}/${work.takes.total}`;
  if (work.planning) return "Planning the shots";
  return work.drawing === null ? null : `Generating images · ${work.drawing.done}/${work.drawing.total}`;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: ClaymationAdDraft): ClaymationForm {
  const inputs = ClaymationAdInputs.safeParse(draft.inputs);
  return { inputs: inputs.success ? inputs.data : ClaymationAdInputs.parse({}), script: draft.script };
}

/** The video's settings live's footer keeps open until "Generate final video" sends them. */
export interface VideoSettings {
  subtitles: boolean;
  caption_position: ClaymationAdInputsOutput["caption_position"];
  caption_style_id: string;
  music_bed: boolean;
  resolution: ClaymationAdInputsOutput["resolution"];
}

/** "Generate final video"'s body (live's hand-off): the approved script and shot spec, the picked
 * take with its check, the packshot, every still by reference, and the footer's settings; null
 * until the storyboard has its spec and every still is drawn. */
export function videoBody(draft: ClaymationAdDraft, settings: VideoSettings) {
  const { script, spec, video_take: take, packshot } = draft;
  if (draft.stage !== "storyboard" || script === null || spec === null || take === null || packshot === null) {
    return null;
  }
  const stills: Partial<Record<ClaymationStillName, OutputRef>> = {};
  for (const still of draft.stills) {
    if (still.status === "done" && still.output != null) stills[still.name] = still.output;
  }
  if (draft.stills.length === 0 || draft.stills.some((still) => stills[still.name] === undefined)) return null;
  return {
    type: "video.template" as const,
    inputs: { template: "claymation-ad" as const, script, spec, take, packshot, stills, ...settings },
  };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function useClaymationDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetClaymationAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<ClaymationForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetClaymationAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: ClaymationAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteClaymationAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveClaymationAdScript(asks);
  const take = useAddClaymationAdTake(asks);
  const voice = useApproveClaymationAdVoice(asks);
  const back = useBackToClaymationAdVoice(asks);
  const retry = useRetryClaymationAdShotDirector(asks);
  const regeneration = useRegenerateClaymationAdStill(asks);
  const reset = useResetClaymationAdDraft(asks);
  const asking = [script, approval, take, voice, back, retry, regeneration, reset].some((each) => each.isPending);
  const work = draft === undefined ? IDLE : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteClaymationAdDraft({
    mutation: {
      // The answer is the draft as kept: what is off in the script as it now stands (live recounts
      // it on every edit) and the narrator by name. A later answer (the wand's) is never undone.
      onSuccess: (answer) => {
        setFailure(null);
        queryClient.setQueryData(key, (current: ClaymationAdDraft | undefined) =>
          current === undefined || current.updated_at <= answer.updated_at ? answer : current,
        );
      },
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: ClaymationAdDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writeClaymationAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = ClaymationAdDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    asking,
    editable,
    failure,
    approving: approval.isPending || voice.isPending,
    resetting: reset.isPending,
    editInputs(change: Partial<ClaymationAdInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    editScript(change: (script: ClaymationScript) => ClaymationScript) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return base.script === null ? base : { ...base, script: change(base.script) };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = ClaymationAdScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = ClaymationAdScriptApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    addTake() {
      ask();
      take.mutate({ teamId, brandId });
    },
    approveVoice(number: number) {
      ask();
      voice.mutate({ teamId, brandId, data: { take: number } });
    },
    backToVoice() {
      ask();
      back.mutate({ teamId, brandId });
    },
    retryShotDirector() {
      ask();
      retry.mutate({ teamId, brandId });
    },
    regenerate(name: ClaymationStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
